import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    FormGroup,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    CustomInput,
    Media,
    CardFooter,
    Pagination,
    PaginationItem,
    PaginationLink,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import Avatar from "components/Common/Avatar.js";
import { getAllAdmins, deleteAdmin, createAdmin, updateAdmin, getAllRoles } from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
    },
    actionBtn: {
        borderRadius: "8px",
        padding: "8px 16px",
        fontWeight: 600,
        letterSpacing: "0.5px"
    },
    moduleBadge: {
        margin: "2px",
        fontSize: "11px",
        fontWeight: 600,
        padding: "5px 10px",
        borderRadius: "6px"
    },
    profileThumb: {
        width: "40px",
        height: "40px",
        borderRadius: "10px",
        objectFit: "cover"
    }
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */

const AdminManagement = ({ showHeader = true, isActive = true }) => {
    const [admins, setAdmins] = useState([]);
    const [roles, setRoles] = useState([]);
    const [modal, setModal] = useState(false);
    const [editingAdmin, setEditingAdmin] = useState(null);
    const [formData, setFormData] = useState({
        au_name: "", au_surname: "", au_email: "", au_password: "", au_phone: "", au_role: "", au_active: "Active"
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const [filterRole, setFilterRole] = useState("All Roles");
    const [filterStatus, setFilterStatus] = useState("All Status");
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        total: 0, page: 1, limit: 10, totalPages: 1,
    });

    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const fetchData = async (page = 1) => {
        try {
            setLoading(true);
            const adminsData = await getAllAdmins(page, pagination.limit, searchTerm);
            const { admins = [], total = 0, totalPages = 1 } = adminsData.payload || {};
            setAdmins(admins);
            setPagination(prev => ({ ...prev, total, page, totalPages }));
        } catch (error) {
            console.log(error);
            setAdmins([]);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const rolesData = await getAllRoles(1, 100);
                setRoles(rolesData.payload?.roles || []);
            } catch (error) {
                console.log("Error loading roles:", error);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        if (isActive) {
            fetchData(pagination.page);
        }
    }, [isActive, pagination.page, searchTerm, filterRole, filterStatus]);

    const toggle = () => {
        setModal(!modal);
        setErrors({});
        if (!modal) {
            setEditingAdmin(null);
        }
    };

    const handleEdit = (admin) => {
        setErrors({});
        setEditingAdmin(admin);
        setFormData({
            au_name: admin.au_name,
            au_surname: admin.au_surname,
            au_email: admin.au_email,
            au_password: "",
            au_phone: admin.au_phone || "",
            au_role: admin.au_role,
            au_active: admin.au_active === "1" ? "Active" : "Inactive"
        });
        setModal(true);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: "Delete Admin?",
            text: "This admin will lose all system access immediately.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteAdmin(id);
                    Swal.fire("Deleted!", "Admin has been removed.", "success");
                    fetchData();
                } catch (error) {
                    Swal.fire("Error!", "Failed to delete admin", "error");
                }
            }
        });
    };

    const validateForm = () => {
        const newErrors = {};
        
        const nameRegex = /^[A-Za-z]+(?:[ ]+[A-Za-z]+)*$/;
        
        // Name validation
        if (!formData.au_name.trim()) {
            newErrors.au_name = "First Name is required";
        } else if (formData.au_name.trim().length > 30) {
            newErrors.au_name = "First Name cannot exceed 30 characters";
        } else if (!nameRegex.test(formData.au_name.trim())) {
            newErrors.au_name = "First Name can only contain letters";
        }

        // Surname validation
        if (formData.au_surname && formData.au_surname.trim()) {
            if (formData.au_surname.trim().length > 30) {
                newErrors.au_surname = "Surname cannot exceed 30 characters";
            } else if (!nameRegex.test(formData.au_surname.trim())) {
                newErrors.au_surname = "Surname can only contain letters";
            }
        }

        // Email validation
        if (!formData.au_email.trim()) {
            newErrors.au_email = "Email Address is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.au_email)) {
            newErrors.au_email = "Invalid email format";
        }

        // Password validation
        if (!editingAdmin && !formData.au_password.trim()) {
            newErrors.au_password = "Password is required for new accounts";
        } else if (formData.au_password && formData.au_password.length < 6) {
            newErrors.au_password = "Password must be at least 6 characters";
        }

        // Role validation
        if (!formData.au_role) newErrors.au_role = "Administrative Role is required";

        // Phone validation
        const phoneRegex = /^\+?[0-9]{10,15}$/;
        if (!formData.au_phone || !formData.au_phone.toString().trim()) {
            newErrors.au_phone = "Phone Number is required";
        } else if (!phoneRegex.test(formData.au_phone.toString().trim())) {
            newErrors.au_phone = "Please enter a valid phone number (10-15 digits)";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        try {
            const payload = {
                ...formData,
                au_active: formData.au_active === "Active" ? "1" : "0"
            };

            if (editingAdmin) {
                await updateAdmin(editingAdmin._id, payload);
                Swal.fire("Updated!", "Admin profile corrected.", "success");
            } else {
                await createAdmin(payload);
                Swal.fire("Success!", "New administrative account created.", "success");
            }
            fetchData();
            toggle();
        } catch (error) {
            Swal.fire("Error!", error.response?.data?.message || error.message || "Something went wrong", "error");
        }
    };

    const filteredAdmins = admins.filter(admin => {
        const matchesRole = filterRole === "All Roles" || admin.role_name === filterRole || admin.au_role === filterRole;
        const statusLabel = admin.au_active === "1" ? "Active" : "Inactive";
        const matchesStatus = filterStatus === "All Status" || statusLabel === filterStatus;
        return matchesRole && matchesStatus;
    });

    return (
        <>
            {showHeader && <CommonHeader />}
            <Container className={showHeader ? "mt--7" : "mt-0"} fluid>
                <Row className="mb-4">
                    <Col className="d-flex justify-content-between align-items-center">
                        <h2 className={showHeader ? "text-white mb-0" : "text-dark mb-0"}>Identity & Access Control</h2>
                        <Button color={showHeader ? "secondary" : "primary"} style={styles.actionBtn} onClick={() => {
                            setEditingAdmin(null);
                            setFormData({ au_name: "", au_surname: "", au_email: "", au_password: "", au_phone: "", au_role: "", au_active: "Active" });
                            toggle();
                        }}>
                            <i className="fas fa-user-plus mr-2" /> Create New Admin
                        </Button>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card className="shadow" style={styles.glassCard}>
                            <CardHeader className="border-0 bg-transparent py-4">
                                <Row className="align-items-center">
                                    <Col>
                                        <h3 className="mb-0 text-dark font-weight-700">
                                            <i className="ni ni-single-02 text-info mr-2" /> Administrative Staff
                                        </h3>
                                        <p className="text-muted text-sm mb-0">Manage system-wide permissions and sidebar module visibility.</p>
                                    </Col>
                                    <Col md="8" className="mt-3 mt-md-0">
                                        <div className="d-flex flex-wrap justify-content-md-end" style={{ gap: "10px" }}>
                                            <Input
                                                placeholder="Search by name or email..."
                                                type="text"
                                                style={{ width: "250px", borderRadius: "8px" }}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            <Input
                                                type="select"
                                                style={{ width: "160px", borderRadius: "8px" }}
                                                value={filterRole}
                                                onChange={(e) => setFilterRole(e.target.value)}
                                            >
                                                <option>All Roles</option>
                                                {roles.map(r => <option key={r.r_uuid} value={r.r_name}>{r.r_name}</option>)}
                                            </Input>
                                            <Input
                                                type="select"
                                                style={{ width: "140px", borderRadius: "8px" }}
                                                value={filterStatus}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option>All Status</option>
                                                <option>Active</option>
                                                <option>Inactive</option>
                                            </Input>
                                        </div>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <div className="table-responsive">
                                <Table className="align-items-center table-flush" hover>
                                    <thead className="thead-light">
                                        <tr>
                                            <th scope="col" style={{ padding: "16px 24px" }}>Admin Profile</th>
                                            <th scope="col">Designation</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="text-right" style={{ paddingRight: "24px" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="4" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>
                                        ) : filteredAdmins.map((admin) => (
                                            <tr key={admin._id}>
                                                <td style={{ padding: "16px 24px" }}>
                                                    <Media className="align-items-center">
                                                        <Avatar
                                                            src={admin.au_image}
                                                            name={`${admin.au_name || ""} ${admin.au_surname || ""}`}
                                                            size="sm"
                                                            className="mr-3"
                                                            style={styles.profileThumb}
                                                        />
                                                        <Media body>
                                                            <span className="mb-0 text-sm font-weight-bold text-dark d-block">
                                                                {admin.au_name} {admin.au_surname}
                                                            </span>
                                                            <span className="text-muted text-xs d-block">{admin.au_email}</span>
                                                        </Media>
                                                    </Media>
                                                </td>
                                                <td>
                                                    <span className="text-dark font-weight-600">{admin.role_name || "Unassigned"}</span>
                                                </td>
                                                <td>
                                                    <Badge color={admin.au_active === "1" ? "success" : "danger"} pill>
                                                        {admin.au_active === "1" ? "Active" : "Inactive"}
                                                    </Badge>
                                                </td>
                                                <td className="text-right" style={{ paddingRight: "24px" }}>
                                                    <Button color="info" outline size="sm" className="mr-2" onClick={() => handleEdit(admin)}>Edit</Button>
                                                    <Button color="danger" outline size="sm" onClick={() => handleDelete(admin._id)}>Delete</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            {/* ✅ Pagination UI */}
                            {admins.length > 0 && (
                                <CardFooter className="py-4 bg-transparent border-0 d-flex justify-content-between align-items-center">
                                    <span className="text-muted small">
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} staff members
                                    </span>
                                    <nav aria-label="...">
                                        <Pagination className="pagination justify-content-end mb-0">
                                            <PaginationItem disabled={pagination.page === 1}>
                                                <PaginationLink
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (pagination.page > 1) fetchData(pagination.page - 1);
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <i className="fas fa-angle-left" />
                                                </PaginationLink>
                                            </PaginationItem>

                                            {renderPaginationItems(pagination.page, pagination.total, pagination.limit, fetchData)}

                                            <PaginationItem disabled={pagination.page === pagination.totalPages}>
                                                <PaginationLink
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (pagination.page < pagination.totalPages) fetchData(pagination.page + 1);
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <i className="fas fa-angle-right" />
                                                </PaginationLink>
                                            </PaginationItem>
                                        </Pagination>
                                    </nav>
                                </CardFooter>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* CREATE/EDIT MODAL */}
            <Modal isOpen={modal} toggle={toggle} centered size="lg">
                <ModalHeader toggle={toggle} className="border-0 pb-0">
                    <span className="text-uppercase text-muted ls-1 font-weight-bold">
                        {editingAdmin ? "Modify Administrative Rights" : "New Admin Account"}
                    </span>
                </ModalHeader>
                <ModalBody className="pt-3">
                    <form onSubmit={handleSubmit} noValidate>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <label className="form-control-label">First Name <span className="text-danger">*</span></label>
                                    <Input
                                        type="text"
                                        placeholder="e.g. John"
                                        value={formData.au_name}
                                        invalid={!!errors.au_name}
                                        onChange={(e) => { setFormData({ ...formData, au_name: e.target.value }); if (errors.au_name) setErrors({ ...errors, au_name: null }) }}
                                    />
                                    {errors.au_name && <small className="text-danger">{errors.au_name}</small>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <label className="form-control-label">Surname</label>
                                    <Input
                                        type="text"
                                        placeholder="e.g. Doe"
                                        required
                                        value={formData.au_surname}
                                        invalid={!!errors.au_surname}
                                        onChange={(e) => {
                                            setFormData({ ...formData, au_surname: e.target.value });
                                            if (errors.au_surname) setErrors({ ...errors, au_surname: null });
                                        }}
                                    />
                                    {errors.au_surname && <small className="text-danger">{errors.au_surname}</small>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <label className="form-control-label">Email Address <span className="text-danger">*</span></label>
                                    <Input
                                        type="email"
                                        placeholder="email@cradle.ai"
                                        value={formData.au_email}
                                        invalid={!!errors.au_email}
                                        onChange={(e) => { setFormData({ ...formData, au_email: e.target.value }); if (errors.au_email) setErrors({ ...errors, au_email: null }) }}
                                    />
                                    {errors.au_email && <small className="text-danger">{errors.au_email}</small>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <label className="form-control-label">Password {(!editingAdmin) && <span className="text-danger">*</span>}</label>
                                    <InputGroup>
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={formData.au_password}
                                            invalid={!!errors.au_password}
                                            onChange={(e) => { setFormData({ ...formData, au_password: e.target.value }); if (errors.au_password) setErrors({ ...errors, au_password: null }) }}
                                        />
                                        <InputGroupAddon addonType="append">
                                            <InputGroupText onClick={() => setShowPassword(!showPassword)} style={{ cursor: "pointer" }}>
                                                <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                                            </InputGroupText>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {errors.au_password && <small className="text-danger">{errors.au_password}</small>}
                                    {editingAdmin && !errors.au_password && <small className="text-muted">Leave blank to keep current password.</small>}
                                </FormGroup>
                            </Col>


                        </Row>
                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <label className="form-control-label">Phone Number <span className="text-danger">*</span></label>
                                    <Input
                                        type="text"
                                        placeholder="e.g. 9876543210"
                                        value={formData.au_phone}
                                        invalid={!!errors.au_phone}
                                        onChange={(e) => {
                                            setFormData({ ...formData, au_phone: e.target.value });
                                            if (errors.au_phone) setErrors({ ...errors, au_phone: null });
                                        }}
                                    />
                                    {errors.au_phone && <small className="text-danger">{errors.au_phone}</small>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="12">
                                <FormGroup>
                                    <label className="form-control-label">Administrative Role <span className="text-danger">*</span></label>
                                    <Input
                                        type="select"
                                        value={formData.au_role}
                                        invalid={!!errors.au_role}
                                        onChange={(e) => { setFormData({ ...formData, au_role: e.target.value }); if (errors.au_role) setErrors({ ...errors, au_role: null }) }}
                                    >
                                        <option value="">Select a Role</option>
                                        {roles.map(r => <option key={r.r_uuid} value={r.r_uuid}>{r.r_name}</option>)}
                                    </Input>
                                    {errors.au_role && <small className="text-danger">{errors.au_role}</small>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <FormGroup className="mb-0">
                                <label className="form-control-label mr-3">Account Status</label>
                                <CustomInput
                                    type="switch"
                                    id="status-switch"
                                    label={formData.au_active}
                                    checked={formData.au_active === "Active"}
                                    onChange={(e) => setFormData({ ...formData, au_active: e.target.checked ? "Active" : "Inactive" })}
                                />
                            </FormGroup>
                            <div>
                                <Button color="secondary" onClick={toggle} className="mr-2">Cancel</Button>
                                <Button color="primary" type="submit">
                                    {editingAdmin ? "Save Changes" : "Create Account"}
                                </Button>
                            </div>
                        </div>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default AdminManagement;
